import { useMemo, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, Skeleton, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { downloadCSV, makeCSV } from '../../components/CustomTableWithPagination/csvExport';
import RetryNotice from './RetryNotice';
import { useDashboardRequest } from './useDashboardRequest';

// The shell of every "what is behind this number" popup (the leads behind a count, the students behind a count): the
// title with the count, who/what/when chips, a search box, sortable columns, 25 rows per page, CSV export, a skeleton
// while it loads and a Retry if it fails. The caller says WHAT the rows are: the URL, the columns and how to search.
//
// Fast: the answer is usually already remembered (the count prefetches it on hover, see CountLink), the table shows 25 rows
// at a time, search / sort / paging all happen in the browser, and the skeleton keeps the popup from jumping while loading.
//
//   columns: [{ key, label, align?, sort(row) -> comparable, render(row) -> node, csv(row) -> value }]
//   The server answers { total, truncated, rows } - total is the number that was clicked.
//   `dataChips(data)` (optional) returns extra chips that depend on the answer.
const SKELETON_ROWS = 6;

const DrilldownDialog = ({
  url, title, chips = [], dataChips, columns, searchText, searchLabel, searchPlaceholder, emptyText, rowKey, csvName, onClose,
}) => {
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading } = useDashboardRequest(url, reloadKey);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null); // { key, dir } - null = the server's order (newest first)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const rows = useMemo(() => (data && data.rows) || [], [data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = q ? rows.filter((r) => searchText(r).includes(q)) : rows;
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) out = [...out].sort((a, b) => {
        const x = col.sort(a); const y = col.sort(b);
        return (x < y ? -1 : x > y ? 1 : 0) * (sort.dir === 'asc' ? 1 : -1);
      });
    }
    return out;
  }, [rows, search, sort, columns, searchText]);

  const pageRows = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const toggleSort = (key) => { setPage(0); setSort((s) => (s && s.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' })); };

  const exportCsv = () => {
    const headers = columns.map((c) => ({ field: c.key, headerName: c.label, valueGetter: ({ row }) => c.csv(row) }));
    downloadCSV(makeCSV({ headers, rows: visible }), csvName);
  };

  const total = data ? data.total : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" scroll="paper" aria-labelledby="drilldown-title">
      <DialogTitle id="drilldown-title" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box>
            <Typography component="div" variant="h6" fontWeight={700}>
              {title}{total !== null ? ` · ${total}` : ''}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
              {chips.map((c, i) => <Chip key={i} size="small" variant={i === 0 ? 'filled' : 'outlined'} label={c} />)}
              {/* chips that need the answer (e.g. an average), shown once it has arrived */}
              {data && dataChips && dataChips(data).filter(Boolean).map((c, i) => <Chip key={`d${i}`} size="small" variant="outlined" color="primary" label={c} />)}
            </Box>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {error && !data && <Box sx={{ p: 2 }}><RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} /></Box>}

        {(data || loading) && !error && (
          <>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ p: 2, pb: 1 }}>
              <TextField
                size="small" placeholder={searchPlaceholder} value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 280 }} inputProps={{ 'aria-label': searchLabel }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
              />
              <Button variant="outlined" size="small" onClick={exportCsv} disabled={!visible.length}>Export CSV</Button>
              {data && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  {visible.length === rows.length ? `${rows.length} shown` : `${visible.length} of ${rows.length} shown`}
                  {data.truncated ? ` (first ${rows.length} of ${data.total})` : ''}
                </Typography>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell key={c.key} align={c.align || 'left'} sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        <TableSortLabel active={!!sort && sort.key === c.key} direction={sort && sort.key === c.key ? sort.dir : 'asc'} onClick={() => toggleSort(c.key)}>
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!data && Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <TableRow key={`s${i}`}>{columns.map((c, ci) => <TableCell key={c.key}><Skeleton variant="text" width={ci === 0 ? 150 : 90} /></TableCell>)}</TableRow>
                  ))}
                  {data && pageRows.map((r) => (
                    <TableRow key={rowKey(r)} hover>
                      {columns.map((c) => <TableCell key={c.key} align={c.align || 'left'}>{c.render(r)}</TableCell>)}
                    </TableRow>
                  ))}
                  {data && visible.length === 0 && (
                    <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">{rows.length === 0 ? emptyText : 'Nothing matches your search.'}</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {data && visible.length > 25 && (
              <TablePagination
                component="div" count={visible.length} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrilldownDialog;
